import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WaterSmartCard({ customerId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); // null while loading
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentText, setConsentText] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/watersmart/dashboard/${customerId}`);
      setStatus(await res.json());
    } catch {
      setStatus({ connected: false });
    }
  }, [customerId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/watersmart/consent-text`)
      .then((res) => res.json())
      .then((data) => setConsentText(data.text || ''))
      .catch(() => setConsentText(''));
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/watersmart/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          email,
          password,
          consent_accepted: consentAccepted,
        }),
      });
      const data = await res.json();
      if (data.connected) {
        setPassword('');
        await fetchStatus();
      } else {
        setError(data.error || 'Could not connect — check your email and password.');
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  if (status === null) {
    return null; // still loading — render nothing rather than flash the connect form
  }

  if (!status.connected) {
    return (
      <div className="rounded-[24px] border border-white/10 bg-slate-950/45 p-6 shadow-xl backdrop-blur-xl">
        <h3 className="text-lg font-black italic uppercase text-white mb-2">Connect Your Water Account</h3>
        <p className="text-sm text-slate-300 mb-4">
          Link your City of Allen WaterSmart account to see your water usage here.
        </p>
        <form onSubmit={handleConnect} className="space-y-3">
          <input
            type="email"
            placeholder="WaterSmart email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full rounded-lg px-3 py-2 text-slate-900"
          />
          <input
            type="password"
            placeholder="WaterSmart password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full rounded-lg px-3 py-2 text-slate-900"
          />
          {consentText && (
            <div className="max-h-32 overflow-y-auto rounded-lg bg-white/5 p-3 text-xs text-slate-300 whitespace-pre-wrap">
              {consentText}
            </div>
          )}
          <label className="flex items-start gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              required
              className="mt-1"
            />
            <span>I have read and agree to the authorization above.</span>
          </label>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <button
            type="submit"
            disabled={connecting || !consentAccepted}
            className="w-full rounded-lg py-2 font-bold uppercase bg-[#ea580c] text-white disabled:opacity-50"
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        </form>
      </div>
    );
  }

  const summary = status.summary;

  return (
    <div className="rounded-[24px] border border-white/10 bg-slate-950/45 p-6 shadow-xl backdrop-blur-xl">
      <h3 className="text-lg font-black italic uppercase text-white mb-2">Water Usage</h3>
      {!status.synced ? (
        <p className="text-sm text-slate-300">
          Your account is connected. Usage data will appear after the next sync.
        </p>
      ) : summary ? (
        <div className="grid grid-cols-2 gap-3 text-white">
          <div>
            <p className="text-xs text-slate-400">Total Gallons</p>
            <p className="text-xl font-semibold">{summary.total.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Readings</p>
            <p className="text-xl font-semibold">{summary.count.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Min (hourly)</p>
            <p className="text-xl font-semibold">{summary.min}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Max (hourly)</p>
            <p className="text-xl font-semibold">{summary.max}</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-300">
          Connected, but the last sync couldn't be summarized. Contact support if this persists.
        </p>
      )}
      {status.synced_at && (
        <p className="text-xs text-slate-400 mt-3">
          Last updated: {new Date(status.synced_at).toLocaleString()}
        </p>
      )}
      <button
        onClick={() => navigate('/water-conservation')}
        className="mt-4 w-full rounded-lg py-2 font-bold uppercase bg-[#1b7abf] text-white"
      >
        View Full Water Dashboard
      </button>
    </div>
  );
}
